import React, { useState, useEffect, ChangeEvent, useContext } from "react";
import {
  Button,
  Card,
  Icons,
  Label,
  Popover,
  TextInput,
  ToggleButton,
  Variants,
} from "@sede-enterprise/shell-ds-react-framework";
import { Col, Row } from "../flexbox/Flex";
import { MultiSelectSearch } from "../multiSelectSearch/MultiSelectSearch";
import styled from "styled-components";
import AppContext from "../applayout/AppContext";

const StyedToggleButton = styled.div`
  margin-left: 5px;
  margin-right: 5px;
  display: inline;
`;

const StyledCountSpan = styled.span`
  background-color: #0097bb;
  width: 20px;
  border-radius: 10px;
  height: 20px;
  margin-left: 5px;
`;

interface FilterInterface {
  searchTerm: string;
  countryIds: string[];
  customerIds: string[];
  airportIds: string[];
  errorTypes?: string[];
  operationalStatusIds?: string[];
  limit: number;
  offSet: number;
  dateFrom: string;
  dateTo: string;
}

interface ParamsInterface {
  changeFilter: (obj: FilterInterface) => void;
  isMITransactions: boolean;
  filters: FilterInterface;
  miSelectedCount?: number | 0;
  onMISubmit?: () => void;
}

interface MultiSelectFilterInterface {
  id: string;
  name: string;
}

interface AirportInterface {
  countryCode: string;
  id: string;
  name: string;
}

export const FilterNav = (params: ParamsInterface) => {
  const [countries, setCountries] = useState<MultiSelectFilterInterface[]>();
  const [airports, setAirports] = useState<MultiSelectFilterInterface[]>();
  const [customers, setCustomers] = useState<MultiSelectFilterInterface[]>();
  const [errors, setErrors] = useState<{ id: string; name: string }[]>();
  const [operationalStatus, setOperationalStatus] =
    useState<MultiSelectFilterInterface[]>();

  const [searchText, setSearchText] = useState("");
  const [isOnLoad, setIsOnLoad] = useState(true);

  const appFilter = useContext(AppContext);

  useEffect(() => {
    if (appFilter) {
      setCountries(JSON.parse(appFilter).countries);
      setAirports(JSON.parse(appFilter).airports);
      setCustomers(JSON.parse(appFilter).customers);
      setErrors(JSON.parse(appFilter).errortypes);
      setOperationalStatus(JSON.parse(appFilter).operationalStatus);
    }
  }, [appFilter]);

  useEffect(() => {
    if (isOnLoad) {
      setIsOnLoad(false);
      const filter = {
        ...params.filters,
      };
      params.changeFilter(filter);
    }
  }, [params.filters?.searchTerm]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      params.changeFilter(params.filters);
    }
  };

  const clearAllSelections = () => {
    const filter: FilterInterface = {
      ...params.filters,
      countryIds: [],
      customerIds: [],
      airportIds: [],
      errorTypes: [],
      operationalStatusIds: [],
      searchTerm: "",
    };
    params.changeFilter(filter);
  };

  const onCountryChange = (selectedCountries: string[]) => {
    const filter = {
      ...params.filters,
      airportIds: [],
      countryIds: selectedCountries,
    };

    if (selectedCountries.length > 0) {
      const tempAiports: AirportInterface[] = [];
      JSON.parse(appFilter).airports.filter((airport: AirportInterface) => {
        selectedCountries.forEach((countryCode: string) => {
          if (countryCode == airport.countryCode) {
            tempAiports.push(airport);
          }
        });
      });
      setAirports(tempAiports);
    } else {
      setAirports(JSON.parse(appFilter).airports);
    }
    params.changeFilter(filter);
  };

  const onCustomerChange = (selectedCustomers: string[]) => {
    const filter = {
      ...params.filters,
      customerIds: selectedCustomers,
    };
    params.changeFilter(filter);
  };

  const onAirportChange = (selectedAirports: string[]) => {
    const filter = {
      ...params.filters,
      airportIds: selectedAirports,
    };
    params.changeFilter(filter);
  };

  const onErrorChange = (selectedErrors: string[]) => {
    const filter = {
      ...params.filters,
      errorTypes: selectedErrors,
    };
    params.changeFilter(filter);
  };

  const onOperationalStatusChange = (selectedOperationalStatus: string[]) => {
    const filter = {
      ...params.filters,
      operationalStatusIds: selectedOperationalStatus,
    };
    params.changeFilter(filter);
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    params.filters.searchTerm = e.target.value;
  };

  return (
    <>
      <Card>
        <Row>
          <Col size="70">
            <Icons.Filter />
            <Popover
              popup={
                <MultiSelectSearch
                  placeholder="Select Country"
                  dataOptions={countries ? countries : []}
                  handleSelectChange={onCountryChange}
                  selectedItems={params.filters.countryIds}
                />
              }
            >
              <StyedToggleButton>
                <ToggleButton>
                  Country{" "}
                  {params.filters.countryIds.length ? (
                    <StyledCountSpan>
                      {params.filters.countryIds.length}
                    </StyledCountSpan>
                  ) : null}
                </ToggleButton>
              </StyedToggleButton>
            </Popover>

            <Popover
              popup={
                <MultiSelectSearch
                  placeholder="Select Site"
                  dataOptions={airports ? airports : []}
                  handleSelectChange={onAirportChange}
                  selectedItems={params.filters.airportIds}
                />
              }
            >
              <StyedToggleButton>
                <ToggleButton>
                  Site{" "}
                  {params.filters.airportIds.length ? (
                    <StyledCountSpan>
                      {params.filters.airportIds.length}
                    </StyledCountSpan>
                  ) : null}
                </ToggleButton>
              </StyedToggleButton>
            </Popover>

            <Popover
              popup={
                <MultiSelectSearch
                  placeholder="Select Customer"
                  dataOptions={customers ? customers : []}
                  handleSelectChange={onCustomerChange}
                  selectedItems={params.filters.customerIds}
                />
              }
            >
              <StyedToggleButton>
                <ToggleButton>
                  Customer{" "}
                  {params.filters.customerIds.length ? (
                    <StyledCountSpan>
                      {params.filters.customerIds.length}
                    </StyledCountSpan>
                  ) : null}
                </ToggleButton>
              </StyedToggleButton>
            </Popover>

            {params.isMITransactions && (
              <Popover
                popup={
                  <MultiSelectSearch
                    placeholder="Select Errors"
                    dataOptions={errors ? errors : []}
                    handleSelectChange={onErrorChange}
                    selectedItems={params.filters?.errorTypes || []}
                  />
                }
              >
                <StyedToggleButton>
                  <ToggleButton>
                    Error Type{" "}
                    {params.filters.errorTypes &&
                    params.filters.errorTypes.length ? (
                      <StyledCountSpan>
                        {params.filters.errorTypes.length}
                      </StyledCountSpan>
                    ) : null}
                  </ToggleButton>
                </StyedToggleButton>
              </Popover>
            )}

            {!params.isMITransactions && (
              <Popover
                popup={
                  <MultiSelectSearch
                    placeholder="Select Operational Status"
                    dataOptions={operationalStatus ? operationalStatus : []}
                    handleSelectChange={onOperationalStatusChange}
                    selectedItems={params.filters.operationalStatusIds || []}
                  />
                }
              >
                <StyedToggleButton>
                  <ToggleButton>
                    Operational Status{" "}
                    {params.filters.operationalStatusIds &&
                    params.filters.operationalStatusIds.length ? (
                      <StyledCountSpan>
                        {params.filters.operationalStatusIds.length}
                      </StyledCountSpan>
                    ) : null}
                  </ToggleButton>
                </StyedToggleButton>
              </Popover>
            )}

            <strong
              onClick={(e) => {
                e.preventDefault();
                clearAllSelections();
              }}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            >
              Clear All
            </strong>

            {params.isMITransactions && (params.miSelectedCount ?? 0) > 0 && (
              <Button
                variant={Variants.Outlined}
                style={{ marginLeft: "15px" }}
                onClick={() => {
                  if (params.onMISubmit) {
                    params.onMISubmit();
                  }
                }}
              >
                Submit
              </Button>
            )}
          </Col>
          <Col size="30">
            <Row>
              <Label bold style={{ paddingRight: "5px" }}>
                Search
              </Label>
              <TextInput
                size="medium"
                onChange={(e) => {
                  onSearchChange(e);
                }}
                suffix={{ node: <Icons.Search /> }}
                placeholder="Search by ticket#"
                value={params.filters.searchTerm}
                title="Search by ticket#"
                id="search"
                onKeyDown={handleKeyDown}
              />
            </Row>
          </Col>
        </Row>
      </Card>
    </>
  );
};
